import React, { useState, useEffect, useRef, useMemo } from "react";
import ReactEcharts from "echarts-for-react";
import * as echarts from "echarts";
import axios from "axios";
import API from "@/api";
import { Empty } from "antd";
import GbiArrow from "../../../../../assets/images/gbiArrow.svg";
import chinaGeo from "@/assets/china.geo.json";
import "./index.less";

// 中国 34 省/直辖市/自治区/特别行政区 简称 -> 全称映射
const NAME_ALIAS = {
  北京: "北京市",
  天津: "天津市",
  上海: "上海市",
  重庆: "重庆市",
  河北: "河北省",
  山西: "山西省",
  辽宁: "辽宁省",
  吉林: "吉林省",
  黑龙江: "黑龙江省",
  江苏: "江苏省",
  浙江: "浙江省",
  安徽: "安徽省",
  福建: "福建省",
  江西: "江西省",
  山东: "山东省",
  河南: "河南省",
  湖北: "湖北省",
  湖南: "湖南省",
  广东: "广东省",
  海南: "海南省",
  四川: "四川省",
  贵州: "贵州省",
  云南: "云南省",
  陕西: "陕西省",
  甘肃: "甘肃省",
  青海: "青海省",
  台湾: "台湾省",
  内蒙古: "内蒙古自治区",
  广西: "广西壮族自治区",
  西藏: "西藏自治区",
  宁夏: "宁夏回族自治区",
  新疆: "新疆维吾尔自治区",
  香港: "香港特别行政区",
  澳门: "澳门特别行政区",
};

// 将后端维度名归一化到 DataV GeoJSON 的全称
const normalizeName = (name) => {
  if (name == null) return "";
  const s = `${name}`.trim();
  if (!s) return "";
  if (NAME_ALIAS[s]) return NAME_ALIAS[s];
  const stripped = s.replace(
    /(省|市|自治区|特别行政区|壮族自治区|回族自治区|维吾尔自治区)$/g,
    "",
  );
  if (NAME_ALIAS[stripped]) return NAME_ALIAS[stripped];
  return s;
};

// 数值转换：兼容数字 / 百分比字符串
const toNumber = (val) => {
  if (typeof val === "number") return val;
  if (typeof val === "string") {
    const v = val.replace("%", "") * 1;
    return isNaN(v) ? 0 : v;
  }
  return 0;
};

const MAP_NAME = "china_gbi";
const MAP_NAME_NANHAI = "china_gbi_nanhai";

// 省名 -> adcode 映射（从 chinaGeo 提取，用于下钻加载市级 GeoJSON）
const NAME_TO_ADCODE = {};
(chinaGeo.features || []).forEach((f) => {
  const p = f.properties || {};
  if (p.name && p.adcode && p.level === "province") {
    NAME_TO_ADCODE[p.name] = p.adcode;
  }
});

// 已加载的市级地图缓存（adcode -> mapName）
const cityMapCache = {};

// 注册中国地图：拆成主体 + 南海诸岛两份
let mapRegistered = false;
const registerChinaMap = () => {
  if (mapRegistered || echarts.getMap(MAP_NAME)) return;
  const allFeatures = chinaGeo.features || [];
  const mainFeatures = [];
  let nanhaiFeature = null;
  allFeatures.forEach((f) => {
    const adcode = f.properties && f.properties.adcode;
    if (adcode === "100000_JD") {
      nanhaiFeature = {
        ...f,
        properties: { ...f.properties, name: "南海诸岛" },
      };
    } else {
      mainFeatures.push(f);
    }
  });
  echarts.registerMap(MAP_NAME, { ...chinaGeo, features: mainFeatures });
  if (nanhaiFeature) {
    echarts.registerMap(MAP_NAME_NANHAI, {
      ...chinaGeo,
      features: [nanhaiFeature],
    });
  }
  mapRegistered = true;
};
registerChinaMap();

// 加载市级 GeoJSON 并注册（带缓存）
const loadCityMap = async (adcode) => {
  const mapName = `city_${adcode}`;
  if (cityMapCache[adcode]) return mapName;
  const res = await fetch(
    `https://geo.datav.aliyun.com/areas_v3/bound/${adcode}_full.json`,
  );
  if (!res.ok) throw new Error("加载市级地图失败");
  const geoJson = await res.json();
  echarts.registerMap(mapName, geoJson);
  cityMapCache[adcode] = mapName;
  return mapName;
};

function MapData(props) {
  const {
    data: initialData = {},
    question_id,
    drillable,
    drillActions,
    changSpining,
  } = props;

  // 历史栈：[{ title, rawData, mapName }]，栈顶为当前显示
  const [historyStack, setHistoryStack] = useState([
    { title: "", rawData: initialData, mapName: MAP_NAME },
  ]);
  // 初始数据变化时重置栈
  useEffect(() => {
    setHistoryStack([{ title: "", rawData: initialData, mapName: MAP_NAME }]);
  }, [initialData]);

  const currentStackItem = historyStack[historyStack.length - 1];
  const currentData = currentStackItem.rawData;
  const currentMapName = currentStackItem.mapName;

  // 防重点击
  const lastClickedDimRef = useRef(null);

  const { metric_fields, data: dData, dim_field } = currentData;

  const [newDrillable, setNewDrillable] = useState(drillable);
  const [dimField, setDimField] = useState(currentData?.dim_field?.field);
  const dimFieldRef = useRef(dimField);
  const drillableRef = useRef(newDrillable);

  useEffect(() => {
    dimFieldRef.current = dimField;
  }, [dimField]);
  useEffect(() => {
    drillableRef.current = newDrillable;
  }, [newDrillable]);
  useEffect(() => {
    setNewDrillable(drillable);
  }, [drillable]);

  useEffect(() => {
    registerChinaMap();
  }, []);

  // 构造地图系列数据: [{ name, value }]
  const mapSeriesData = useMemo(() => {
    const rows = dData || [];
    return rows.map((row) => ({
      name: normalizeName(row[0]),
      value: toNumber(row[1]),
    }));
  }, [dData]);

  const metricName = metric_fields?.[0]?.name || dim_field?.name || "数值";
  const metricUnit = metric_fields?.[0]?.unit || "";

  // 数值范围，用于 visualMap
  const valueExtent = useMemo(() => {
    const values = mapSeriesData.map((d) => d.value).filter((v) => !isNaN(v));
    if (!values.length) return [0, 0];
    return [Math.min(...values), Math.max(...values)];
  }, [mapSeriesData]);

  // 是否为全国级（根节点）
  const isRootLevel = historyStack.length === 1;

  // 点击下钻
  const handleChartClick = async (params) => {
    if (params.componentType !== "series" && params.componentType !== "geo")
      return;
    const clickedDimName = params.name;
    if (lastClickedDimRef.current === clickedDimName) return;

    if (newDrillable !== true && newDrillable !== "true") return;

    lastClickedDimRef.current = clickedDimName;
    changSpining(true);

    try {
      // 1. 调后端接口获取下一层数据
      const res = await axios.post(API.getFilterAndNextData, {
        question_id,
        dim_field: dimFieldRef.current,
        dim_value: clickedDimName || "",
      });
      const newData = res?.data?.chart_data || {};
      const newDrill = res?.data?.drillable;

      // 2. 加载市级 GeoJSON（根据点击的省名查 adcode）
      const fullName = normalizeName(clickedDimName);
      const adcode = NAME_TO_ADCODE[fullName];
      let cityMapName = MAP_NAME;
      if (adcode) {
        try {
          cityMapName = await loadCityMap(adcode);
        } catch (e) {
          console.error("市级地图加载失败，回退全国地图", e);
          cityMapName = MAP_NAME;
        }
      }

      // 3. 压入历史栈
      setNewDrillable(newDrill);
      setDimField(newData?.dim_field?.field);
      setHistoryStack((prev) => {
        const firstItem = prev[0] || {
          title: "",
          rawData: initialData,
        };
        return [
          { title: "", rawData: firstItem.rawData, mapName: MAP_NAME },
          ...prev.slice(1),
          { title: clickedDimName, rawData: newData, mapName: cityMapName },
        ];
      });
    } catch (err) {
      console.error("下钻请求失败", err);
    } finally {
      changSpining(false);
    }
  };

  // 面包屑返回
  const handleBreadcrumbClick = (index) => {
    if (index === historyStack.length - 1) return;
    setHistoryStack((prev) => {
      const next = prev.slice(0, index + 1);
      if (index === 0) {
        return [{ ...next[0], title: "" }];
      }
      return next;
    });
    lastClickedDimRef.current = "";
    setNewDrillable(true);
    setDimField(historyStack[index]?.rawData?.dim_field?.field);
  };

  const getOptions = () => {
    const baseTooltip = {
      trigger: "item",
      formatter: function (params) {
        const raw = params.value;
        if (raw == null || isNaN(raw)) {
          return `${params.name}<br/>${metricName}: 暂无数据`;
        }
        return `${params.name}<br/>${metricName}: ${Number(
          raw,
        ).toLocaleString()}${metricUnit}`;
      },
    };
    const baseVisualMap = {
      min: valueExtent[0],
      max: valueExtent[1],
      left: "left",
      top: "bottom",
      text: ["高", "低"],
      calculable: true,
      inRange: {
        color: ["#cfe8f5", "#b7d6f3", "#40a9ed", "#3598c1", "#215096"],
      },
      textStyle: { fontSize: 11 },
    };

    // 全国级：含南海诸岛小图
    if (isRootLevel) {
      return {
        tooltip: baseTooltip,
        visualMap: baseVisualMap,
        graphic: {
          type: "group",
          right: 28,
          bottom: 28,
          children: [
            {
              type: "rect",
              left: 0,
              top: 0,
              shape: { width: 120, height: 170, r: 4 },
              style: {
                fill: "rgba(255, 255, 255, 0.92)",
                stroke: "#0d3b78",
                lineWidth: 1.2,
              },
            },
            {
              type: "text",
              left: 60,
              top: 8,
              style: {
                text: "南海诸岛",
                fill: "#0d3b78",
                fontSize: 12,
                fontWeight: "bold",
                textAlign: "center",
              },
            },
          ],
        },
        geo: [
          {
            map: MAP_NAME,
            aspectScale: 0.75,
            zoom: 1.4,
            center: [104, 36],
            roam: false,
            itemStyle: {
              areaColor: "#e8eef5",
              borderColor: "#0d3b78",
              borderWidth: 1,
            },
            label: {
              show: true,
              color: "#0d3b78",
              fontSize: 9,
              formatter: (p) =>
                p.name.replace(
                  /(省|市|自治区|特别行政区|壮族自治区|回族自治区|维吾尔自治区)$/,
                  "",
                ),
            },
            emphasis: {
              areaColor: "#40a9ed",
              borderWidth: 1.2,
              label: {
                show: true,
                color: "#fff",
                fontSize: 11,
                fontWeight: "bold",
              },
            },
          },
          {
            map: MAP_NAME_NANHAI,
            right: 34,
            bottom: 34,
            width: 96,
            height: 130,
            roam: false,
            zoom: 0.9,
            aspectScale: 0.5,
            itemStyle: {
              areaColor: "#e8eef5",
              borderColor: "#0d3b78",
              borderWidth: 1,
            },
            emphasis: {
              areaColor: "#40a9ed",
              borderColor: "#0d3b78",
              borderWidth: 1.2,
              label: { show: false },
            },
            label: { show: false },
          },
        ],
        series: [
          {
            name: metricName,
            type: "map",
            map: MAP_NAME,
            geoIndex: 0,
            data: mapSeriesData,
            emphasis: {
              label: { show: true, color: "#fff", fontSize: 12 },
            },
          },
        ],
      };
    }

    // 市级：单地图，无南海诸岛小图
    return {
      tooltip: baseTooltip,
      visualMap: baseVisualMap,
      geo: {
        map: currentMapName,
        aspectScale: 0.75,
        roam: false,
        itemStyle: {
          areaColor: "#f5f7fa",
          borderColor: "#0d3b78",
          borderWidth: 1,
        },
        label: {
          show: true,
          color: "#0d3b78",
          fontSize: 10,
        },
        emphasis: {
          areaColor: "#40a9ed",
          borderWidth: 1.2,
          label: {
            show: true,
            color: "#fff",
            fontSize: 12,
            fontWeight: "bold",
          },
        },
      },
      series: [
        {
          name: metricName,
          type: "map",
          map: currentMapName,
          geoIndex: 0,
          data: mapSeriesData,
          emphasis: {
            label: { show: true, color: "#fff", fontSize: 12 },
          },
        },
      ],
    };
  };

  if (!dData || !dData.length) {
    return (
      <div className="mapLoading">
        <Empty />
      </div>
    );
  }

  return (
    <>
      <ReactEcharts
        key={JSON.stringify(currentData) + currentMapName}
        style={{
          minHeight: "480px",
          maxHeight: "560px",
          width: "100%",
          overflow: "hidden",
        }}
        option={getOptions()}
        notMerge={true}
        lazyUpdate={true}
        onEvents={
          newDrillable === true || newDrillable === "true"
            ? { click: handleChartClick }
            : undefined
        }
      />
      {/* 面包屑导航 */}
      <div
        className="historyStack"
        style={{
          padding: drillable === "true" || drillable === true ? "" : "unset",
        }}
      >
        <div>
          {historyStack.map((item, index) => (
            <span key={index}>
              <span
                style={
                  index === historyStack.length - 1
                    ? {}
                    : { cursor: "pointer", color: "#1C69FC" }
                }
                onClick={() => handleBreadcrumbClick(index)}
              >
                {item.title}
              </span>
              {index < historyStack.length - 1 && (
                <span style={{ padding: "0 10px" }}>
                  <img style={{ width: "8px" }} src={GbiArrow} alt=" " />
                </span>
              )}
            </span>
          ))}
        </div>
        <div style={{ color: "#aaa" }}>
          {drillable === "true" || drillable === true
            ? "点击省份可下钻"
            : undefined}
        </div>
      </div>
    </>
  );
}

export default MapData;
